import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Download } from "lucide-react";
import { getTakeawayQrCode, getTakeawaySettings, saveTakeawaySettings } from "./takeawayApi";
import { downloadTakeawayQrPoster } from "./takeawayQrPoster";
import { CUSTOMER_MENU_URL } from "../../Utills/config";
import { ButtonLoader, PageLoader } from "../../component/common/Loader";
import "./takeaway.css";

const defaults = { isEnabled: false, isAcceptingOrders: false, averagePreparationMinutes: 30, allowPayAtPickup: true, allowOnlinePayment: false, packagingCharge: 0, minimumOrderAmount: 0, autoAcceptOrders: false, allowScheduledPickup: false, maximumScheduleDays: 7, orderingHoursJson: null };

const SettingToggle = ({ label, checked, onChange }) => (
  <button
    type="button"
    className="takeaway-setting-toggle"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
  >
    <span className="takeaway-setting-toggle-label">{label}</span>
    <span className="takeaway-setting-toggle-track" aria-hidden="true">
      <span className="takeaway-setting-toggle-thumb" />
    </span>
  </button>
);

const TakeawaySettings = () => {
  const user = JSON.parse(localStorage.getItem("admin_token_info") || "{}");
  const restaurantSlug = encodeURIComponent(user.restaurantName || "restaurant");
  const publicUrl = `${CUSTOMER_MENU_URL.replace(/\/$/, "")}/${restaurantSlug}/${user.restaurantId}/takeaway`;
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingQr, setDownloadingQr] = useState(false);
  useEffect(() => { getTakeawaySettings().then((data) => setForm({ ...defaults, ...data })).catch((error) => toast.error(error.message)).finally(() => setLoading(false)); }, []);
  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try { setForm(await saveTakeawaySettings({ ...form, averagePreparationMinutes: Number(form.averagePreparationMinutes), packagingCharge: Number(form.packagingCharge), minimumOrderAmount: Number(form.minimumOrderAmount), maximumScheduleDays: Number(form.maximumScheduleDays) })); toast.success("Takeaway settings saved."); }
    catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };
  const downloadQrCode = async () => {
    setDownloadingQr(true);
    try {
      const blob = await getTakeawayQrCode(publicUrl);
      await downloadTakeawayQrPoster(blob, user.restaurantName || "Restaurant");
      toast.success("Branded takeaway QR downloaded.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloadingQr(false);
    }
  };
  if (loading) return <div className="takeaway-page"><PageLoader label="Loading takeaway settings..." /></div>;
  return <div className="takeaway-page takeaway-settings-page">
    <div className="takeaway-title takeaway-settings-title">
      <div>
        <h1>Takeaway Settings</h1>
        <p>Controls staff and customer takeaway availability</p>
        </div>
      </div>
      <form className="takeaway-settings" onSubmit={submit}>
    <div className="takeaway-public-link"><div><strong>Customer takeaway link</strong><span>Share this link or download its QR code.</span></div><input readOnly value={publicUrl} /><div className="takeaway-actions"><button type="button" onClick={() => navigator.clipboard.writeText(publicUrl).then(() => toast.success("Takeaway link copied."))}>Copy link</button><a href={publicUrl} target="_blank" rel="noreferrer">Open</a><button type="button" className="takeaway-download-qr" disabled={downloadingQr} onClick={downloadQrCode}><Download size={17} aria-hidden="true" />{downloadingQr ? "Downloading…" : "Download QR code"}</button></div></div>
    <div className="takeaway-switches">
      <SettingToggle label="Enable Takeaway" checked={form.isEnabled} onChange={(value) => update("isEnabled", value)} />
      <SettingToggle label="Accepting orders" checked={form.isAcceptingOrders} onChange={(value) => update("isAcceptingOrders", value)} />
      <SettingToggle label="Auto-accept orders" checked={form.autoAcceptOrders} onChange={(value) => update("autoAcceptOrders", value)} />
      <SettingToggle label="Pay at pickup" checked={form.allowPayAtPickup} onChange={(value) => update("allowPayAtPickup", value)} />
      <SettingToggle label="Online payment" checked={form.allowOnlinePayment} onChange={(value) => update("allowOnlinePayment", value)} />
      <SettingToggle label="Scheduled pickup" checked={form.allowScheduledPickup} onChange={(value) => update("allowScheduledPickup", value)} />
    </div>
    <div className="takeaway-field-row"><label>Preparation minutes<input type="number" min="5" max="240" value={form.averagePreparationMinutes} onChange={(e) => update("averagePreparationMinutes", e.target.value)} /></label><label>Packaging charge<input type="number" min="0" step="0.01" value={form.packagingCharge} onChange={(e) => update("packagingCharge", e.target.value)} /></label></div>
    <div className="takeaway-field-row"><label>Minimum order amount<input type="number" min="0" step="0.01" value={form.minimumOrderAmount} onChange={(e) => update("minimumOrderAmount", e.target.value)} /></label><label>Maximum schedule days<input type="number" min="1" max="30" value={form.maximumScheduleDays} disabled={!form.allowScheduledPickup} onChange={(e) => update("maximumScheduleDays", e.target.value)} /></label></div>
    <button className="takeaway-primary" disabled={saving} aria-busy={saving}>{saving ? <ButtonLoader label="Saving..." /> : "Save settings"}</button>
  </form></div>;
};

export default TakeawaySettings;
